import { useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { PICKER_HANDOFF_MS, type TPickerHandoffPhase, pickerHandoffShowsStep } from "./picker-intro";
import type { TQuestionnairePath } from "./questionnaire-flow";

/**
 * After the first picker click: bubble → selected tiles → path step 1.
 * Deep-link and reduced motion jump to the selected tiles + step 1.
 * Changing journey later stays on `ready` (no replay).
 */
export function usePickerHandoff(path: TQuestionnairePath | null): {
	phase: TPickerHandoffPhase;
	showStep: boolean;
} {
	const reducedMotion = Boolean(useReducedMotion());
	const startedWithPathRef = useRef(path !== null);
	const handedOffRef = useRef(path !== null);
	const prevPathRef = useRef<TQuestionnairePath | null>(path);
	const [phase, setPhase] = useState<TPickerHandoffPhase>(() => (path ? "ready" : "idle"));

	useEffect(() => {
		if (!path) {
			startedWithPathRef.current = false;
			handedOffRef.current = false;
			prevPathRef.current = null;
			setPhase("idle");
			return;
		}

		const pathChanged = prevPathRef.current !== null && prevPathRef.current !== path;
		prevPathRef.current = path;

		if (startedWithPathRef.current || handedOffRef.current || reducedMotion || pathChanged) {
			handedOffRef.current = true;
			setPhase("ready");
			return;
		}

		setPhase("bubble");
		const tilesTimer = window.setTimeout(() => {
			setPhase("tiles");
		}, PICKER_HANDOFF_MS.bubble);
		const readyTimer = window.setTimeout(() => {
			handedOffRef.current = true;
			setPhase("ready");
		}, PICKER_HANDOFF_MS.bubble + PICKER_HANDOFF_MS.tiles);

		return () => {
			window.clearTimeout(tilesTimer);
			window.clearTimeout(readyTimer);
		};
	}, [path, reducedMotion]);

	return { phase, showStep: pickerHandoffShowsStep(phase) };
}
